import { supabase } from '../lib/supabase';
import { trackingService } from './trackingService';

class RealtimeMonitoringService {
  constructor() {
    this.listeners = [];
    this.alertThresholds = {
      errorRate: 5, // errors per minute
      dbWriteFailureRate: 3, // failures per minute
      displayFailureRate: 3 // failures per minute
    };
  }

  // Track database write operations
  async trackDatabaseWrite(operation) {
    const startTime = performance.now();
    const operationId = `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;

    try {
      const logData = {
        operation_id: operationId,
        operation_type: operation?.type, // 'create', 'update', 'delete'
        table_name: operation?.tableName,
        record_id: operation?.recordId || null,
        user_id: operation?.userId || null,
        status: 'started',
        started_at: new Date()?.toISOString(),
        metadata: operation?.metadata || {}
      };

      // Log to tracking service
      await trackingService?.logActivity({
        activityType: 'database_write_started',
        activityDescription: `Database ${operation?.type} on ${operation?.tableName}`,
        metadata: logData
      });

      return {
        operationId,
        complete: async (success, error = null, recordId = null) => {
          const endTime = performance.now();
          const duration = endTime - startTime;

          const completionData = {
            ...logData,
            operation_id: operationId,
            status: success ? 'success' : 'failed',
            completed_at: new Date()?.toISOString(),
            duration_ms: Math.round(duration),
            error_message: error?.message || null,
            error_stack: error?.stack || null,
            record_id: recordId || logData?.record_id
          };

          // Log completion
          await trackingService?.logActivity({
            activityType: success ? 'database_write_success' : 'database_write_failed',
            activityDescription: `Database ${operation?.type} ${success ? 'succeeded' : 'failed'} on ${operation?.tableName}`,
            metadata: completionData
          });

          // Log performance
          await trackingService?.logPerformance({
            metricType: 'database_operation',
            metricName: `${operation?.type}_${operation?.tableName}`,
            durationMs: Math.round(duration),
            success,
            errorMessage: error?.message || null,
            metadata: completionData
          });

          // If failed, log error and check for alerts
          if (!success) {
            await trackingService?.logError({
              errorType: 'DatabaseWriteError',
              errorMessage: error?.message || 'Database write operation failed',
              errorStack: error?.stack || null,
              metadata: completionData
            });

            // Check if we need to send alert
            await this.checkAndSendAlert('database_write_failure', completionData);
          }

          // Notify listeners
          this.notifyListeners({
            type: 'database_write',
            success,
            data: completionData
          });

          return completionData;
        }
      };
    } catch (error) {
      console.error('Error tracking database write:', error);
      return {
        operationId,
        complete: async () => {}
      };
    }
  }

  // Track display failures (when data fails to load or render)
  async trackDisplayFailure(failure) {
    try {
      const failureData = {
        component_name: failure?.componentName,
        failure_type: failure?.type, // 'data_load_failed', 'render_error', 'missing_data'
        error_message: failure?.error?.message || failure?.message,
        error_stack: failure?.error?.stack || null,
        page_url: window.location?.href,
        user_id: failure?.userId || null,
        metadata: {
          ...failure?.metadata,
          timestamp: new Date()?.toISOString()
        }
      };

      // Log error
      await trackingService?.logError({
        errorType: 'DisplayFailure',
        errorMessage: failureData?.error_message,
        errorStack: failureData?.error_stack,
        metadata: failureData
      });

      // Log activity
      await trackingService?.logActivity({
        activityType: 'display_failure',
        activityDescription: `Display failure in ${failure?.componentName}: ${failure?.type}`,
        metadata: failureData
      });

      // Check if we need to send alert
      await this.checkAndSendAlert('display_failure', failureData);

      // Notify listeners
      this.notifyListeners({
        type: 'display_failure',
        data: failureData
      });

      return failureData;
    } catch (error) {
      console.error('Error tracking display failure:', error);
    }
  }

  // Track player registration save operations
  async trackPlayerRegistrationSave(playerData) {
    const operation = await this.trackDatabaseWrite({
      type: 'create',
      tableName: 'player_master_profiles',
      userId: playerData?.userId,
      metadata: {
        playerName: playerData?.fullName,
        position: playerData?.primaryPosition,
        hasPhoto: !!playerData?.photoFile
      }
    });

    return operation;
  }

  // Check error rates and send alerts if thresholds exceeded
  async checkAndSendAlert(alertType, data) {
    try {
      const oneMinuteAgo = new Date(Date.now() - 60000)?.toISOString();

      // Get recent errors of this type
      let recentErrors = [];
      
      if (alertType === 'database_write_failure') {
        const { data: activities } = await supabase?.from('user_activity')?.select('*')?.eq('activity_type', 'database_write_failed')?.gte('created_at', oneMinuteAgo);
        recentErrors = activities || [];
      } else if (alertType === 'display_failure') {
        const { data: errors } = await supabase?.from('error_logs')?.select('*')?.eq('error_type', 'DisplayFailure')?.gte('created_at', oneMinuteAgo);
        recentErrors = errors || [];
      }

      const errorCount = recentErrors?.length;
      const threshold = alertType === 'database_write_failure' 
        ? this.alertThresholds?.dbWriteFailureRate 
        : this.alertThresholds?.displayFailureRate;

      if (errorCount >= threshold) {
        await this.sendAlert({
          severity: 'high',
          type: alertType,
          message: `${alertType?.replace('_', ' ')} threshold exceeded: ${errorCount} errors in the last minute`,
          errorCount,
          threshold,
          recentErrors: recentErrors?.slice(0, 5), // Include last 5 errors
          metadata: data
        });
      }
    } catch (error) {
      console.error('Error checking alert thresholds:', error);
    }
  }

  // Send alert notification
  async sendAlert(alert) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const alertData = {
        user_id: user?.id || null,
        alert_type: alert?.type,
        severity: alert?.severity,
        message: alert?.message,
        error_count: alert?.errorCount,
        threshold: alert?.threshold,
        metadata: {
          ...alert?.metadata,
          recentErrors: alert?.recentErrors
        },
        resolved: false,
        created_at: new Date()?.toISOString()
      };

      // Log as critical error
      await trackingService?.logError({
        errorType: 'SystemAlert',
        errorMessage: alert?.message,
        metadata: alertData
      });

      // Notify listeners
      this.notifyListeners({
        type: 'alert',
        severity: alert?.severity,
        data: alertData
      });

      console.warn('🚨 SYSTEM ALERT:', alert?.message);
      
      return alertData;
    } catch (error) {
      console.error('Error sending alert:', error);
    }
  }

  // Subscribe to real-time monitoring events
  subscribe(callback) {
    this.listeners?.push(callback);
    return () => {
      this.listeners = this.listeners?.filter(cb => cb !== callback);
    };
  }

  // Notify all listeners
  notifyListeners(event) {
    this.listeners?.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in monitoring listener:', error);
      }
    });
  }

  // Get monitoring statistics
  async getMonitoringStats(timeRange = '1h') {
    try {
      const timeRanges = {
        '1h': 3600000,
        '24h': 86400000,
        '7d': 604800000
      };

      const startTime = new Date(Date.now() - timeRanges[timeRange])?.toISOString();

      // Get database write stats
      const { data: dbWrites } = await supabase?.from('user_activity')?.select('*')?.in('activity_type', ['database_write_success', 'database_write_failed'])?.gte('created_at', startTime);

      const dbWriteSuccess = dbWrites?.filter(w => w?.activity_type === 'database_write_success')?.length || 0;
      const dbWriteFailed = dbWrites?.filter(w => w?.activity_type === 'database_write_failed')?.length || 0;

      // Get display failures
      const { data: displayFailures } = await supabase?.from('error_logs')?.select('*')?.eq('error_type', 'DisplayFailure')?.gte('created_at', startTime);

      // Get all errors
      const { data: allErrors } = await supabase?.from('error_logs')?.select('*')?.gte('created_at', startTime);

      // Get performance metrics
      const { data: perfMetrics } = await supabase?.from('performance_metrics')?.select('*')?.eq('metric_type', 'database_operation')?.gte('created_at', startTime);

      const avgDbWriteTime = perfMetrics?.length > 0
        ? perfMetrics?.reduce((sum, m) => sum + (m?.duration_ms || 0), 0) / perfMetrics?.length
        : 0;

      return {
        timeRange,
        databaseWrites: {
          total: (dbWriteSuccess + dbWriteFailed),
          successful: dbWriteSuccess,
          failed: dbWriteFailed,
          successRate: dbWriteSuccess + dbWriteFailed > 0 
            ? ((dbWriteSuccess / (dbWriteSuccess + dbWriteFailed)) * 100)?.toFixed(2)
            : 100,
          avgDuration: Math.round(avgDbWriteTime)
        },
        displayFailures: {
          total: displayFailures?.length || 0,
          byType: this.groupByType(displayFailures || [])
        },
        errors: {
          total: allErrors?.length || 0,
          unresolved: allErrors?.filter(e => !e?.resolved)?.length || 0,
          byType: this.groupByType(allErrors || [])
        }
      };
    } catch (error) {
      console.error('Error getting monitoring stats:', error);
      return null;
    }
  }

  groupByType(items) {
    return items?.reduce((acc, item) => {
      const type = item?.error_type || item?.failure_type || 'unknown';
      acc[type] = (acc?.[type] || 0) + 1;
      return acc;
    }, {});
  }

  // Get recent monitoring events
  async getRecentEvents(limit = 50) {
    try {
      // Get recent activities
      const { data: activities } = await supabase?.from('user_activity')?.select('*')?.in('activity_type', ['database_write_success', 'database_write_failed', 'display_failure'])?.order('created_at', { ascending: false })?.limit(limit);

      // Get recent errors
      const { data: errors } = await supabase?.from('error_logs')?.select('*')?.order('created_at', { ascending: false })?.limit(limit);

      // Combine and sort by timestamp
      const allEvents = [
        ...(activities || [])?.map(a => ({
          ...a,
          eventType: 'activity',
          timestamp: a?.created_at
        })),
        ...(errors || [])?.map(e => ({
          ...e,
          eventType: 'error',
          timestamp: e?.created_at
        }))
      ]?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      return allEvents?.slice(0, limit);
    } catch (error) {
      console.error('Error getting recent events:', error);
      return [];
    }
  }
}

export const realtimeMonitoringService = new RealtimeMonitoringService();